import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Calculator, PartyPopper, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { KineticLines } from "../components/Kinetic";
import { Seo } from "../components/Seo";
import { API, BUSINESS } from "../data/site";

const RATE = 90;

const LAWN_SIZES = [
    { id: "small", label: "Small", desc: "Courtyard or small yard", hours: [0.75, 1.25] },
    { id: "medium", label: "Medium", desc: "Standard family backyard", hours: [1.25, 2] },
    { id: "large", label: "Large", desc: "Big block, front and back", hours: [2, 3] },
    { id: "xlarge", label: "Very large", desc: "Acreage-style or corner block", hours: [3, 4.5] },
];
const LAWN_CONDITIONS = [
    { id: "maintained", label: "Well maintained", mult: 1 },
    { id: "overgrown", label: "Overgrown", mult: 1.5 },
    { id: "very", label: "Very overgrown", mult: 2 },
];
const WINDOW_SIZES = [
    { id: "small", label: "Small home or unit", hours: [1, 1.5] },
    { id: "medium", label: "Standard 3-bed home", hours: [1.5, 2.5] },
    { id: "large", label: "Large or two-storey", hours: [2.5, 4] },
];
const GUTTER_SIDES = [
    { id: 1, label: "1 side" },
    { id: 2, label: "2 sides" },
    { id: 3, label: "3 sides" },
    { id: 4, label: "4+ sides" },
];
const JUNK_LOADS = [
    { id: "small", label: "A few items", hours: [0.5, 1], tip: 40 },
    { id: "half", label: "Half a trailer", hours: [1, 1.5], tip: 75 },
    { id: "full", label: "Full trailer load", hours: [1.5, 2.5], tip: 150 },
    { id: "multi", label: "Multiple loads", hours: [3, 5], tip: 300 },
];

const round5 = (n) => Math.round(n / 5) * 5;
const money = (n) => `$${n.toLocaleString("en-AU")}`;

const inputCls = "w-full rounded-xl border border-forest/20 bg-bone-card px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-forest focus:ring-2 focus:ring-forest/15";

const OptionGrid = ({ options, value, onPick, testidPrefix, cols = "sm:grid-cols-3" }) => (
    <div className={`grid gap-3 ${cols}`}>
        {options.map((o) => {
            const active = value === o.id;
            return (
                <button type="button" key={o.id} onClick={() => onPick(o.id)} data-testid={`${testidPrefix}-${o.id}`} aria-pressed={active}
                    className={`rounded-2xl border px-4 py-3.5 text-left transition-colors ${
                        active ? "border-forest bg-forest text-bone" : "border-forest/20 bg-bone-card text-ink hover:border-forest/50"
                    }`}>
                    <span className="block text-sm font-bold">{o.label}</span>
                    {o.desc && <span className={`mt-0.5 block text-xs ${active ? "text-bone/75" : "text-moss"}`}>{o.desc}</span>}
                </button>
            );
        })}
    </div>
);

export default function Estimate() {
    const [services, setServices] = useState([]);
    const [lawnSize, setLawnSize] = useState(null);
    const [lawnCond, setLawnCond] = useState(null);
    const [windowSize, setWindowSize] = useState(null);
    const [gutterSides, setGutterSides] = useState(null);
    const [junkLoad, setJunkLoad] = useState(null);
    const [result, setResult] = useState(null);
    const [form, setForm] = useState({ full_name: "", phone: "", email: "", address: "" });
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);

    const toggleService = (s) =>
        setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

    const ready =
        services.length > 0 &&
        (!services.includes("Lawn Care") || (lawnSize && lawnCond)) &&
        (!services.includes("Window Cleaning") || windowSize) &&
        (!services.includes("Gutter Cleaning") || gutterSides) &&
        (!services.includes("Junk Removal") || junkLoad);

    const calculate = () => {
        const lines = [];
        let low = 0;
        let high = 0;
        if (services.includes("Lawn Care")) {
            const size = LAWN_SIZES.find((o) => o.id === lawnSize);
            const cond = LAWN_CONDITIONS.find((o) => o.id === lawnCond);
            const lo = size.hours[0] * cond.mult * RATE;
            const hi = size.hours[1] * cond.mult * RATE;
            low += lo; high += hi;
            lines.push(`Lawn Care: ${size.label.toLowerCase()} lawn, ${cond.label.toLowerCase()} (~${(size.hours[0] * cond.mult).toFixed(1)}–${(size.hours[1] * cond.mult).toFixed(1)} hrs @ $${RATE}/hr)`);
        }
        if (services.includes("Gutter Cleaning")) {
            const lo = gutterSides * 20;
            const hi = gutterSides * 45;
            low += lo; high += hi;
            lines.push(`Gutter Cleaning: ${gutterSides} side${gutterSides > 1 ? "s" : ""} @ $20–$45 per side`);
        }
        if (services.includes("Window Cleaning")) {
            const size = WINDOW_SIZES.find((o) => o.id === windowSize);
            low += size.hours[0] * RATE; high += size.hours[1] * RATE;
            lines.push(`Window Cleaning: ${size.label.toLowerCase()} (~${size.hours[0]}–${size.hours[1]} hrs @ $${RATE}/hr)`);
        }
        if (services.includes("Junk Removal")) {
            const load = JUNK_LOADS.find((o) => o.id === junkLoad);
            low += load.hours[0] * RATE + load.tip; high += load.hours[1] * RATE + load.tip;
            lines.push(`Junk Removal: ${load.label.toLowerCase()} (~${load.hours[0]}–${load.hours[1]} hrs @ $${RATE}/hr + ~$${load.tip} tipping)`);
        }
        setResult({ low: round5(low), high: round5(high), lines });
        setDone(false);
        setTimeout(() => document.getElementById("estimate-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/bookings`, {
                ...form,
                services,
                request_type: "quote",
                preferred_date: new Date().toISOString().split("T")[0],
                preferred_time: "Any time",
                budget: `${money(result.low)} – ${money(result.high)} (instant estimate)`,
                notes: `INSTANT ESTIMATE: ${money(result.low)} – ${money(result.high)} (rough guide only)\n${result.lines.join("\n")}\nCustomer is flexible on date and time.`,
            });
            setDone(true);
            window.scrollTo(0, 0);
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Something went wrong — please try again or call us.");
        } finally {
            setSending(false);
        }
    };

    if (done) {
        return (
            <main data-testid="estimate-page" className="pt-16">
                <section className="mx-auto max-w-2xl px-6 py-24 text-center lg:py-32">
                    <Reveal>
                        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-forest text-bone">
                            <PartyPopper size={26} />
                        </span>
                        <h1 className="mt-8 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">Quote request received</h1>
                        <p data-testid="estimate-success-message" className="mt-5 rounded-2xl border border-forest/15 bg-bone-subtle p-6 text-base leading-relaxed text-ink">
                            Thanks! Your rough estimate was <span className="font-bold">{money(result.low)} – {money(result.high)}</span>.
                            We&apos;ll contact you shortly to confirm your free, exact quote.
                        </p>
                        <p className="mt-4 text-sm text-moss">
                            Need it sooner? Call us on <a href={BUSINESS.phoneHref} className="font-bold text-forest">{BUSINESS.phone}</a>.
                        </p>
                        <Link to="/" data-testid="estimate-success-home-button"
                            className="mt-8 inline-block rounded-full bg-forest px-7 py-3.5 text-sm font-bold text-bone transition-colors hover:bg-forest-deep">
                            Back to Home
                        </Link>
                    </Reveal>
                </section>
            </main>
        );
    }

    return (
        <main data-testid="estimate-page" className="pt-16">
            <Seo title="Instant Price Estimate | Lawn Busters Adelaide"
                description="Answer a few quick questions and get a rough price range for lawn mowing, gutters, windows or junk removal in Adelaide — then lock in your free exact quote." />
            <section className="mx-auto max-w-3xl px-6 py-16 lg:py-24">
                <p className="eyebrow flex items-center gap-2 text-forest-leaf"><Calculator size={12} /> Instant estimate</p>
                <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl">
                    <KineticLines lines={["What Will It", "Cost, Roughly?"]} lineClassName="last:text-forest-leaf" />
                </h1>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-moss">
                    Answer a few quick questions and get a rough price range in seconds. Every property is
                    different, so your final free quote is always confirmed in person — no obligation.
                </p>

                <div className="mt-12 space-y-10">
                    <div>
                        <p className="eyebrow mb-4 text-forest">1 · What do you need done?</p>
                        <div className="grid gap-3 sm:grid-cols-2">
                            {["Lawn Care", "Gutter Cleaning", "Window Cleaning", "Junk Removal"].map((s) => {
                                const active = services.includes(s);
                                return (
                                    <button type="button" key={s} onClick={() => toggleService(s)}
                                        data-testid={`estimate-service-${s.toLowerCase().replace(/ /g, "-")}`} aria-pressed={active}
                                        className={`flex items-center justify-between rounded-2xl border px-5 py-4 text-left text-sm font-bold transition-colors ${
                                            active ? "border-forest bg-forest text-bone" : "border-forest/20 bg-bone-card text-ink hover:border-forest/50"
                                        }`}>
                                        {s}
                                        <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${active ? "border-bone bg-bone text-forest" : "border-forest/30 text-transparent"}`}>
                                            <Check size={13} />
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {services.includes("Lawn Care") && (
                        <div data-testid="estimate-lawn-questions">
                            <p className="eyebrow mb-4 text-forest">2 · How big is the lawn?</p>
                            <OptionGrid options={LAWN_SIZES} value={lawnSize} onPick={setLawnSize} testidPrefix="estimate-lawn-size" cols="sm:grid-cols-2" />
                            <p className="eyebrow mb-4 mt-6 text-forest">And how&apos;s it looking?</p>
                            <OptionGrid options={LAWN_CONDITIONS} value={lawnCond} onPick={setLawnCond} testidPrefix="estimate-lawn-condition" />
                        </div>
                    )}

                    {services.includes("Gutter Cleaning") && (
                        <div data-testid="estimate-gutter-questions">
                            <p className="eyebrow mb-4 text-forest">How many sides of the house have gutters?</p>
                            <OptionGrid options={GUTTER_SIDES} value={gutterSides} onPick={setGutterSides} testidPrefix="estimate-gutter-side" cols="grid-cols-2 sm:grid-cols-4" />
                        </div>
                    )}

                    {services.includes("Window Cleaning") && (
                        <div data-testid="estimate-window-questions">
                            <p className="eyebrow mb-4 text-forest">What size is the property?</p>
                            <OptionGrid options={WINDOW_SIZES} value={windowSize} onPick={setWindowSize} testidPrefix="estimate-window-size" />
                        </div>
                    )}

                    {services.includes("Junk Removal") && (
                        <div data-testid="estimate-junk-questions">
                            <p className="eyebrow mb-4 text-forest">How much junk is there?</p>
                            <OptionGrid options={JUNK_LOADS} value={junkLoad} onPick={setJunkLoad} testidPrefix="estimate-junk-load" cols="sm:grid-cols-2" />
                        </div>
                    )}

                    <button type="button" onClick={calculate} disabled={!ready} data-testid="estimate-calculate-button"
                        className="rounded-full bg-ochre px-8 py-4 text-sm font-bold text-forest-night transition-colors hover:bg-clay hover:text-bone disabled:cursor-not-allowed disabled:opacity-40">
                        Calculate My Estimate <ArrowRight size={14} className="ml-1 inline" />
                    </button>
                    {!ready && services.length === 0 && (
                        <p className="-mt-6 text-xs text-moss">Pick at least one service to get started.</p>
                    )}
                </div>

                {result && (
                    <div id="estimate-result" data-testid="estimate-result" className="mt-14 scroll-mt-24">
                        <Reveal>
                            <div className="rounded-3xl bg-forest-night p-8 text-bone lg:p-10">
                                <p className="eyebrow text-ochre">Your rough estimate</p>
                                <p data-testid="estimate-result-range" className="mt-3 font-display text-4xl font-bold tracking-tight text-bone sm:text-5xl">
                                    {money(result.low)} – {money(result.high)}
                                </p>
                                <ul className="mt-6 space-y-2 border-t border-bone/10 pt-6">
                                    {result.lines.map((l) => (
                                        <li key={l} className="text-sm leading-relaxed text-bone/70">{l}</li>
                                    ))}
                                </ul>
                                {result.high >= 600 && (
                                    <p className="mt-4 text-xs text-bone/60">Jobs valued $600+ ask for a 25% deposit to lock in your booking.</p>
                                )}
                                <p className="mt-4 text-xs leading-relaxed text-bone/60">
                                    Rough guide only — every property is different. Your final quote is free,
                                    confirmed in person, and there&apos;s no obligation.
                                </p>
                            </div>
                        </Reveal>

                        <Reveal delay={0.1}>
                            <form onSubmit={submit} data-testid="estimate-contact-form" className="mt-10 rounded-3xl border border-forest/10 bg-bone-card p-8">
                                <h2 className="font-display text-xl font-bold tracking-tight text-ink sm:text-2xl">Lock in your free exact quote</h2>
                                <p className="mt-2 text-sm text-moss">Leave your details and we&apos;ll confirm the exact price — no obligation.</p>
                                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                                    <div>
                                        <label className="eyebrow mb-2 block text-forest" htmlFor="est-name">Full name</label>
                                        <input id="est-name" data-testid="estimate-name-input" required className={inputCls}
                                            value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Your full name" />
                                    </div>
                                    <div>
                                        <label className="eyebrow mb-2 block text-forest" htmlFor="est-phone">Phone number</label>
                                        <input id="est-phone" data-testid="estimate-phone-input" required type="tel" className={inputCls}
                                            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="04xx xxx xxx" />
                                    </div>
                                    <div>
                                        <label className="eyebrow mb-2 block text-forest" htmlFor="est-email">Email</label>
                                        <input id="est-email" data-testid="estimate-email-input" required type="email" className={inputCls}
                                            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
                                    </div>
                                    <div>
                                        <label className="eyebrow mb-2 block text-forest" htmlFor="est-suburb">Suburb</label>
                                        <input id="est-suburb" data-testid="estimate-suburb-input" required className={inputCls}
                                            value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="e.g. Happy Valley" />
                                    </div>
                                </div>
                                <button type="submit" disabled={sending} data-testid="estimate-submit-button"
                                    className="mt-7 w-full rounded-full bg-forest px-8 py-4 text-sm font-bold text-bone transition-colors hover:bg-forest-deep disabled:opacity-60 sm:w-auto">
                                    {sending ? "Sending..." : "Get My Free Exact Quote"}
                                </button>
                            </form>
                        </Reveal>
                    </div>
                )}
            </section>
        </main>
    );
}
